import { api } from "./api";
import { getAll as get } from "./pagination";

export interface Sport {
  id: number;
  name: string;
  description?: string | null;
  sport_type: string;
  minimum_participants: number;
  maximum_participants: number;
  is_active: boolean;
  is_competitive: boolean;
  rules?: string | null;
  safety_guidelines?: string | null;
}
export interface Team {
  id: number;
  name: string;
  sport: number;
  sport_name: string;
  coach: number;
  coach_name: string;
  captain?: number | null;
  captain_name?: string;
  season?: string | null;
  year: number;
  team_motto?: string | null;
  team_colors?: string | null;
  description?: string | null;
  is_active: boolean;
}
export interface Member {
  id: number;
  team: number;
  team_name: string;
  student: number;
  student_name: string;
  position: string;
  jersey_number?: number | null;
  joined_date: string;
  left_date?: string | null;
  is_active: boolean;
  games_played: number;
  goals_scored: number;
  assists: number;
  removal_reason?: string;
}
export interface Competition {
  id: number;
  name: string;
  description?: string | null;
  competition_type: string;
  sport: number;
  sport_name: string;
  start_date: string;
  end_date: string;
  venue?: string | null;
  organizer?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  prizes?: string | null;
  is_active: boolean;
  is_completed: boolean;
  status: "scheduled" | "in_progress" | "completed" | "cancelled";
  cancellation_reason?: string;
}
export interface Match {
  id: number;
  competition: number;
  competition_name: string;
  match_type: "group_stage" | "knockout" | "final" | "semi_final" | "quarter_final" | "friendly";
  home_team: number;
  home_team_name: string;
  away_team: number;
  away_team_name: string;
  match_date: string;
  match_time: string;
  venue?: string | null;
  referee?: string | null;
  status: "scheduled" | "in_progress" | "completed" | "cancelled" | "postponed";
  home_score?: number | null;
  away_score?: number | null;
  winner?: number | null;
  winner_name?: string;
  duration_minutes?: number | null;
  notes?: string | null;
  cancellation_reason?: string;
  postponement_reason?: string;
}
export interface MatchEvent {
  id: number;
  match: number;
  event_type: string;
  player: number;
  player_name: string;
  minute: number;
  description?: string | null;
  recorded_by: number;
}
export interface Training {
  id: number;
  team: number;
  team_name: string;
  session_date: string;
  start_time: string;
  end_time: string;
  venue?: string | null;
  focus_area?: string | null;
  objectives?: string | null;
  conducted_by_name: string;
  attendance_count: number;
  is_completed: boolean;
  status: "scheduled" | "in_progress" | "completed" | "cancelled";
  notes?: string | null;
  cancellation_reason?: string;
}
export interface Attendance {
  id: number;
  training_session: number;
  training_session_name: string;
  student: number;
  student_name: string;
  attended: boolean;
  performance_rating?: number | null;
  notes?: string | null;
}
export interface Achievement {
  id: number;
  student: number;
  student_name: string;
  team?: number | null;
  team_name?: string;
  competition?: number | null;
  competition_name?: string;
  achievement_type: string;
  title: string;
  description?: string | null;
  achieved_date: string;
  award_level?: string | null;
  certificate_number?: string | null;
}
export interface Equipment {
  id: number;
  name: string;
  description?: string | null;
  equipment_type: string;
  sport?: number | null;
  sport_name?: string;
  total_quantity: number;
  available_quantity: number;
  damaged_quantity: number;
  storage_location?: string | null;
  requires_maintenance: boolean;
  is_active: boolean;
}
export interface EquipmentTransaction {
  id: number;
  equipment: number;
  equipment_name: string;
  transaction_type: string;
  quantity: number;
  available_change: number;
  damaged_change: number;
  reason: string;
  recorded_by_name: string;
  created_at: string;
}
export interface StudentOption {
  id: number;
  student_id: string;
  full_name: string;
  class_name?: string | null;
  class_level_order?: number | null;
  gender?: string;
}
export interface SportsStats {
  active_sports: number;
  active_teams: number;
  active_members: number;
  competitions: number;
  upcoming_matches: number;
  training_sessions: number;
  achievements: number;
}

type Payload = Record<string, unknown>;

export const sportsService = {
  getSports: () => get<Sport>("/sports/sports/"),
  createSport: async (data: Payload) => (await api.post("/sports/sports/", data)).data as Sport,
  updateSport: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/sports/sports/${id}/`, data)).data as Sport,
  getTeams: () => get<Team>("/sports/teams/"),
  createTeam: async (data: Payload) => (await api.post("/sports/teams/", data)).data as Team,
  updateTeam: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/sports/teams/${id}/`, data)).data as Team,
  getMembers: () => get<Member>("/sports/team-members/"),
  getTeamMembers: (team: number) => get<Member>(`/sports/team-members/?team=${team}`),
  createMember: async (data: Payload) => (await api.post("/sports/team-members/", data)).data as Member,
  removeMember: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/sports/team-members/${id}/remove/`, { reason })).data as Member,
  getCompetitions: () => get<Competition>("/sports/competitions/"),
  createCompetition: async (data: Payload) => (await api.post("/sports/competitions/", data)).data as Competition,
  startCompetition: async (id: number) => (await api.post(`/sports/competitions/${id}/start/`)).data as Competition,
  completeCompetition: async (id: number) => (await api.post(`/sports/competitions/${id}/complete/`)).data as Competition,
  cancelCompetition: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/sports/competitions/${id}/cancel/`, { reason })).data as Competition,
  getMatches: () => get<Match>("/sports/matches/"),
  createMatch: async (data: Payload) => (await api.post("/sports/matches/", data)).data as Match,
  startMatch: async (id: number) => (await api.post(`/sports/matches/${id}/start/`)).data as Match,
  completeMatch: async ({ id, data }: { id: number; data: Payload }) => (await api.post(`/sports/matches/${id}/complete/`, data)).data as Match,
  cancelMatch: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/sports/matches/${id}/cancel/`, { reason })).data as Match,
  postponeMatch: async ({ id, data }: { id: number; data: Payload }) => (await api.post(`/sports/matches/${id}/postpone/`, data)).data as Match,
  getMatchEvents: () => get<MatchEvent>("/sports/match-events/"),
  getEventsFor: (match: number) => get<MatchEvent>(`/sports/match-events/?match=${match}`),
  createMatchEvent: async (data: Payload) => (await api.post("/sports/match-events/", data)).data as MatchEvent,
  getTraining: () => get<Training>("/sports/training-sessions/"),
  createTraining: async (data: Payload) => (await api.post("/sports/training-sessions/", data)).data as Training,
  startTraining: async (id: number) => (await api.post(`/sports/training-sessions/${id}/start/`)).data as Training,
  completeTraining: async ({ id, notes }: { id: number; notes: string }) => (await api.post(`/sports/training-sessions/${id}/complete/`, { notes })).data as Training,
  cancelTraining: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/sports/training-sessions/${id}/cancel/`, { reason })).data as Training,
  getAttendance: () => get<Attendance>("/sports/training-attendance/"),
  getSessionAttendance: (session: number) => get<Attendance>(`/sports/training-attendance/?training_session=${session}`),
  markAttendance: async (data: Payload) => (await api.post("/sports/training-attendance/", data)).data as Attendance,
  getAchievements: () => get<Achievement>("/sports/achievements/"),
  createAchievement: async (data: Payload) => (await api.post("/sports/achievements/", data)).data as Achievement,
  getEquipment: () => get<Equipment>("/sports/equipment/"),
  createEquipment: async (data: Payload) => (await api.post("/sports/equipment/", data)).data as Equipment,
  updateEquipment: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/sports/equipment/${id}/`, data)).data as Equipment,
  transactEquipment: async ({ id, data }: { id: number; data: Payload }) =>
    (await api.post(`/sports/equipment/${id}/transact/`, data)).data as { equipment: Equipment; transaction: EquipmentTransaction },
  getEquipmentLedger: (id: number) => get<EquipmentTransaction>(`/sports/equipment/${id}/ledger/`),
  getStudents: () => get<StudentOption>("/students/service-directory/"),
  getStats: async () => (await api.get("/sports/statistics/")).data as SportsStats,
};
